import { RequestUploadDtoMimeTypeEnum } from '@/types/Api';

export const ALLOWED_FILE_TYPES = Object.values(RequestUploadDtoMimeTypeEnum);
export const MAX_FILE_SIZE = 10 * 1024 * 1024;
export const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.tiff'];
export const FILE_INPUT_ACCEPT = ALLOWED_FILE_EXTENSIONS.join(',');

export interface FileValidationResult {
  isValid: boolean;
  errorKey?: string;
  errorParams?: Record<string, string | number>;
}

/**
 * Validates a file's type and size
 *
 * @param file - The file to validate
 * @param maxSize - Optional maximum file size in bytes (defaults to MAX_FILE_SIZE)
 * @returns Validation result with isValid flag and optional translation key + params
 */
export function validateFile(file: File, maxSize: number = MAX_FILE_SIZE): FileValidationResult {
  // Validate file type
  if (!ALLOWED_FILE_TYPES.includes(file.type as RequestUploadDtoMimeTypeEnum)) {
    return {
      isValid: false,
      errorKey: 'common.file_validation.invalid_type',
      errorParams: {
        type: file.type || 'unknown',
      },
    };
  }

  // Validate file size
  if (file.size > maxSize) {
    const fileSizeMB = Math.round(file.size / 1024 / 1024);
    const maxSizeMB = Math.round(maxSize / 1024 / 1024);
    return {
      isValid: false,
      errorKey: 'common.file_validation.too_large',
      errorParams: {
        size: fileSizeMB,
        maxSize: maxSizeMB,
      },
    };
  }

  return { isValid: true };
}

/**
 * Get human-readable list of supported file formats
 */
export function getSupportedFormatsText(): string {
  return ALLOWED_FILE_TYPES.map((type) => type.split('/')[1].toUpperCase()).join(', ');
}
